import { db, firebase } from "../firebaseConfig";
import { ExtensionFirebaseSchema } from "../@types/sharedSchema";
import {
  presenceFromListeners,
  type ExtensionOutcome,
  type ExtensionPresence
} from "./extensionPresence";

export {
  describePresence,
  isSafeToRetry,
  presenceFromListeners,
  type ExtensionOutcome,
  type ExtensionPresence
} from "./extensionPresence";

/** Same window as the server driver (whatsappExtension.service.ts `EXTENSION_OPERATION_TTL_MS`). */
const EXTENSION_OPERATION_TTL_MS = 2 * 24 * 60 * 60 * 1000;

/**
 * How long a job may wait for a WhatsApp tab to take it. A ready tab takes one in about a second, even in
 * a background tab; past this nobody will, and the job is cancelled — which also means nobody can take
 * it later (no late sends). Nothing was sent, so the caller may queue the same message again.
 */
const PICKUP_WAIT_MS = 30_000;
/**
 * How long a TAKEN job may take to finish. A media send in a background tab measured up to 167 s; a big
 * contacts load up to 157 s. Past this the outcome is "unknown": it may have been sent.
 */
const RESULT_WAIT_MS = 5 * 60_000;

/** How many of the account's most recently active tabs are read. */
const PRESENCE_TABS = 5;
/** Leases age without a new snapshot, so the account's status is recomputed this often. */
const PRESENCE_RECHECK_MS = 30_000;
/** After a job nobody took, a "ready" that is not fresher than the miss is trusted again after this. */
const READY_AFTER_MISS_MS = 60_000;

type Operation = ExtensionFirebaseSchema.WhatsappExtensionOperation;

export type ExtensionOperationResult = Partial<Operation> & {
  outcome: ExtensionOutcome;
  /** What happened, in Hebrew for the realtor (set where the operation's own error is not readable). */
  realtorMessage?: string;
  /**
   * An `unknown` job of an extension older than 1.0.93: it may still send it, and would not look in the
   * chat before a resend — so it is never queued again automatically, only by the realtor after checking.
   */
  neverResendAutomatically?: boolean;
};

const NO_ANSWER_MESSAGE =
  "WhatsApp לא החזיר תשובה תוך 5 דקות. ייתכן שההודעה נשלחה: בדקו ב-WhatsApp לפני שליחה חוזרת";
const OLD_EXTENSION_MISSED_MESSAGE =
  "לשונית WhatsApp Web לא קיבלה את ההודעה תוך 30 שניות. ייתכן שהיא רדומה ותשלח אותה כשתתעורר: רעננו את הלשונית ובדקו בצ'אט לפני שליחה חוזרת";

/**
 * Utility class for interacting with the WhatsApp extension through Firebase
 * instead of direct server calls.
 */
export class ExtensionWithFirebase {
  private readonly operationsCollectionPath = ExtensionFirebaseSchema.WHATSAPP_OPERATIONS_COLLECTION;
  private apiToken: string;

  constructor(apiToken: string) {
    this.apiToken = apiToken;
  }

  /** The account's most recently active tabs; by creation time only if the lease index is not there yet. */
  private listenersQuery(byLease = true) {
    const base = db.collection(ExtensionFirebaseSchema.WHATSAPP_LISTENERS_COLLECTION).where("apiToken", "==", this.apiToken);
    return (byLease ? base.orderBy("forcedUpdatedTime", "desc") : base.orderBy("createdTime", "desc")).limit(
      byLease ? PRESENCE_TABS : 10
    );
  }

  private static listenersOf(snapshot: firebase.firestore.QuerySnapshot) {
    return snapshot.docs.map((doc) => ({ ...(doc.data() as ExtensionFirebaseSchema.WhatsappExtensionListener), id: doc.id }));
  }

  /** The account's status right now (one read). */
  async getPresence(): Promise<ExtensionPresence> {
    let snapshot: firebase.firestore.QuerySnapshot;
    try {
      snapshot = await this.listenersQuery().get();
    } catch (error) {
      console.warn("Reading extension presence by lease failed, falling back to creation order:", error);
      snapshot = await this.listenersQuery(false).get();
    }
    return presenceFromListeners(ExtensionWithFirebase.listenersOf(snapshot), Date.now());
  }

  /**
   * Live status: calls back with the account's status on every change of its tabs, and every 30 s as
   * leases age. One listener, about one read per tab change — instead of a database job every 4 seconds.
   */
  subscribeToPresence(callback: (presence: ExtensionPresence) => void): () => void {
    let listeners: ReturnType<typeof ExtensionWithFirebase.listenersOf> = [];
    let unsubscribe = () => {};
    const emit = () => callback(presenceFromListeners(listeners, Date.now()));
    const listen = (byLease: boolean) => {
      unsubscribe = this.listenersQuery(byLease).onSnapshot(
        (snapshot) => {
          listeners = ExtensionWithFirebase.listenersOf(snapshot);
          emit();
        },
        (error) => {
          console.warn("Extension presence listener failed:", error);
          if (byLease) listen(false);
          else emit();
        }
      );
    };
    listen(true);
    const recheck = setInterval(emit, PRESENCE_RECHECK_MS);
    return () => {
      clearInterval(recheck);
      unsubscribe();
    };
  }

  /**
   * Resolves true once the account has a ready tab, false if `isCancelled()` turns true first. With
   * `since` (the time a job went unanswered), a "ready" that the tab has not re-confirmed after that
   * moment is trusted only after a minute: the tab may have died without a word (a laptop closed).
   */
  waitUntilReady(options: {
    since?: number;
    isCancelled?: () => boolean;
    onWaiting?: (presence: ExtensionPresence) => void;
  } = {}): Promise<boolean> {
    const { since, isCancelled, onWaiting } = options;
    return new Promise((resolve) => {
      let done = false;
      let latest: ExtensionPresence | null = null;
      const finish = (value: boolean) => {
        if (done) return;
        done = true;
        clearInterval(poll);
        stop();
        resolve(value);
      };
      const evaluate = () => {
        if (isCancelled?.()) return finish(false);
        if (!latest) return;
        const confirmed =
          !since || (latest.lastSeenAt ?? 0) > since || Date.now() - since >= READY_AFTER_MISS_MS;
        if (latest.state === "ready" && confirmed) return finish(true);
        onWaiting?.(latest);
      };
      const stop = this.subscribeToPresence((presence) => {
        latest = presence;
        evaluate();
      });
      const poll = setInterval(evaluate, 1000);
    });
  }

  /**
   * Creates a job for the account's WhatsApp tabs and waits for it, protocol v2:
   *  1. `queuedAt` is the server's clock; the rules refuse a claim 2 minutes after it.
   *  2. Up to 30 s for a tab to take it. If none did, it is cancelled — only if still untaken, in one
   *     transaction — so nobody can take it later: the outcome is `not_picked_up` and nothing was sent.
   *  3. Once taken, up to 5 minutes for the result; past that the outcome is `unknown` (never cancelled:
   *     the tab may still be sending).
   * Addressed to a tab older than 1.0.93, a job nobody took is `unknown`, not `not_picked_up`: such a tab
   * sends it anyway when it wakes from sleep, cancelled or not.
   */
  private async executeOperation(
    operation: ExtensionFirebaseSchema.WhatsappOperation,
    operationRequestDetails?: any
  ): Promise<ExtensionOperationResult> {
    let activeGuid: string | null = null;
    let toOldExtension = false;
    try {
      const presence = await this.getPresence();
      activeGuid = presence.activeGuid;
      toOldExtension = presence.legacy;
    } catch (error) {
      console.error("Error reading extension presence:", error);
    }

    if (!activeGuid) {
      return { outcome: "not_picked_up", operationStatus: "failed", operationError: "No active whatsapp listener found" };
    }

    try {
      const now = new Date();
      const docRef = await db.collection(this.operationsCollectionPath).add({
        apiToken: this.apiToken,
        // Extensions before 1.0.93 take only jobs addressed to them; later ones take any job of the account.
        activeGuid,
        operation,
        operationStatus: "pending",
        ...(operationRequestDetails ? { operationRequestDetails } : {}),
        queuedAt: firebase.firestore.FieldValue.serverTimestamp(),
        createdTime: now,
        updatedTime: now,
        // A Firestore TTL policy deletes the document after this. Until the extension signs in the
        // collection is readable by anyone, and every operation carries this account's apiToken.
        expireAt: new Date(now.getTime() + EXTENSION_OPERATION_TTL_MS)
      });

      const result = await this.waitForOperation(docRef, toOldExtension);
      if (result.outcome !== "success" || !result.operationResponseUrl) return result;

      try {
        const response = await fetch(result.operationResponseUrl);
        if (!response.ok) throw new Error(`Failed to fetch from URL: ${response.status} ${response.statusText}`);
        return { ...result, operationResponseObject: await response.json() };
      } catch (fetchError) {
        console.error(`Error fetching data from URL: ${result.operationResponseUrl}`, fetchError);
        return {
          ...result,
          operationResponseObject: { error: true, message: `Failed to fetch data from URL: ${fetchError.message}` }
        };
      }
    } catch (error) {
      console.error(`Error executing ${operation} operation via Firebase:`, error);
      return { outcome: "not_picked_up", operationStatus: "failed", operationError: error.message || "Unknown error" };
    }
  }

  private waitForOperation(
    docRef: firebase.firestore.DocumentReference,
    toOldExtension: boolean
  ): Promise<ExtensionOperationResult> {
    return new Promise((resolve) => {
      let settled = false;
      let resultTimer: ReturnType<typeof setTimeout> | null = null;
      const finish = (result: ExtensionOperationResult) => {
        if (settled) return;
        settled = true;
        clearTimeout(pickupTimer);
        if (resultTimer) clearTimeout(resultTimer);
        unsubscribe();
        resolve(result.outcome === "unknown" && toOldExtension ? { ...result, neverResendAutomatically: true } : result);
      };

      const unsubscribe = docRef.onSnapshot(
        (doc) => {
          const data = doc.data() as Operation | undefined;
          if (!data) return finish({ outcome: "not_picked_up", operationStatus: "failed", operationError: "Operation document not found" });
          if (data.operationStatus !== "pending" && !resultTimer) {
            resultTimer = setTimeout(
              () =>
                finish({
                  ...data,
                  id: doc.id,
                  outcome: "unknown",
                  operationStatus: "failed",
                  operationError: NO_ANSWER_MESSAGE,
                  realtorMessage: NO_ANSWER_MESSAGE
                }),
              RESULT_WAIT_MS
            );
          }
          if (data.operationStatus === "success") return finish({ ...data, id: doc.id, outcome: "success" });
          if (data.operationStatus === "failed") {
            const outcome: ExtensionOutcome =
              data.operationErrorCode === "NOT_READY" ? "not_ready" : data.operationErrorCode === "UNKNOWN_OUTCOME" ? "unknown" : "failed";
            return finish({ ...data, id: doc.id, outcome });
          }
        },
        (error) => finish({ outcome: "unknown", operationStatus: "failed", operationError: error.message })
      );

      const pickupTimer = setTimeout(async () => {
        try {
          const cancelled = await db.runTransaction(async (transaction) => {
            const current = await transaction.get(docRef);
            if (current.data()?.operationStatus !== "pending") return false;
            transaction.update(docRef, { operationStatus: "cancelled" });
            return true;
          });
          if (cancelled && toOldExtension) {
            finish({
              id: docRef.id,
              outcome: "unknown",
              operationStatus: "failed",
              operationError: OLD_EXTENSION_MISSED_MESSAGE,
              realtorMessage: OLD_EXTENSION_MISSED_MESSAGE
            });
          } else if (cancelled) {
            finish({
              id: docRef.id,
              outcome: "not_picked_up",
              operationStatus: "failed",
              operationError: "אף לשונית WhatsApp Web לא קיבלה את ההודעה תוך 30 שניות, והיא לא נשלחה"
            });
          }
        } catch (error) {
          // Could not cancel (network): keep waiting for the snapshot; the result timer bounds it if taken.
          console.error("Cancelling an untaken extension job failed:", error);
        }
      }, PICKUP_WAIT_MS);
    });
  }

  /**
   * Sends a WhatsApp message using the extension via Firebase
   * @param toPhoneNumber The recipient's phone number (without the + prefix)
   * @param message The message text to send
   * @param mediaLink Optional URL to media to attach to the message
   * @param customFileName Optional custom filename for the media
   * @param dedupeSince On a retry of a message whose first attempt may have gone out (outcome `unknown`):
   *   that attempt's time, in Unix seconds. The extension (1.0.93+) then looks in the chat first and does
   *   not send it twice.
   */
  async sendWhatsAppMessage(
    toPhoneNumber: string,
    message: string,
    mediaLink: string = "",
    customFileName: string = "",
    dedupeSince?: number
  ): Promise<ExtensionOperationResult> {
    const requestDetails: ExtensionFirebaseSchema.SendMessageOperationRequestDetails = {
      toPhoneNumber,
      message,
      mediaLink,
      customFileName,
      ...(dedupeSince ? { dedupeSince } : {})
    };

    return this.executeOperation("SEND_MESSAGE", requestDetails);
  }

  /**
   * Gets contacts from WhatsApp
   * @param shouldReturnSlimResponse Whether to return a slim response
   * @param getOnlyContacts Whether to get only contacts
   * @param onlySavedContacts Ask for the phone's saved contacts only. The extension (1.0.92+)
   *   then reads the address book instead of every contact record it holds, which is what
   *   keeps the load short on a heavy account. An older extension ignores the flag and
   *   returns everything, so filter on each row's `isMyContact` anyway.
   * @returns A promise that resolves to the operation result
   */
  async getWhatsAppContacts(
    shouldReturnSlimResponse: boolean = false,
    getOnlyContacts: boolean = true,
    onlySavedContacts: boolean = false
  ): Promise<ExtensionOperationResult> {
    const requestDetails: ExtensionFirebaseSchema.GetContactsRequestDetails = {
      shouldReturnSlimResponse,
      getOnlyContacts,
      onlySavedContacts
    };

    return this.executeOperation("GET_CONTACTS", requestDetails);
  }

  /**
   * Gets groups from WhatsApp
   * @returns A promise that resolves to the operation result
   */
  async getWhatsAppGroups(): Promise<ExtensionOperationResult> {
    return this.executeOperation("GET_GROUPS", {});
  }

  /**
   * Gets avatar for a WhatsApp contact
   * @param phone The phone number to get the avatar for
   * @param imgFull Whether to get the full image
   * @returns A promise that resolves to the operation result
   */
  async getWhatsAppAvatar(phone: string, imgFull: boolean = false): Promise<ExtensionOperationResult> {
    const requestDetails: ExtensionFirebaseSchema.GetAvatarRequestDetails = {
      phone,
      imgFull
    };

    return this.executeOperation("GET_AVATAR", requestDetails);
  }
}

// Export a function to create an instance of the class
export const createExtensionWithFirebase = (apiToken: string) => {
  return new ExtensionWithFirebase(apiToken);
};
